import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import CopyFieldButton from '@rsos/components/capstone/DataCards/CopyFieldButton';
import CustomSafeImageRenderer from '@rsos/components/capstone/DataCards/CustomSafeImageRenderer';
import {
  Renderer,
  TypeString,
  TypeObject,
  TypeArray,
  validatedPayload,
} from '@rsos/ellington';
import { useGetDisplaySchemaQuery } from '@rsos/reduxDataStore/src/features/displaySchema/displaySchemaApi';
import { captureExceptionWithScope } from '@rsos/utils/sentry';
import { trackAdrDataDisplay } from '../../../gainsightTrackings/eventPaneTrackings';
import CustomImageRenderer from '../CustomImageRenderer';
import CustomVideoRenderer from '../CustomVideoRenderer';
import CustomActionableButton from './CustomActionableButton';
import CustomCard from './CustomCard';
import { SUPPORTED_CUSTOM_COMPONENTS } from './CustomComponents/constants';
import { getComponentToRender } from './CustomComponents/utils';
import CustomDataLink from './CustomDataLink';
import CustomList from './CustomList';
import CustomVideo from './CustomVideo';
import ErrorBoundary from './ErrorBoundary';
import selectADRForCall from './selectADRForCall';

export const findData = (data, path = []) => {
  if (typeof data !== 'object' || path.length === 0) {
    return data;
  }
  const idxOrKey = path[0];
  const currData = data[idxOrKey];
  return findData(currData, path.slice(1));
};

export const EllingtonCard = ({
  data,
  display_schema,
  showDisclaimer,
  source,
  displaySchemaUrl,
  dataCategory,
  dataSuffix,
  showExitButton = true,
  onFetchDataLink = () => {},
  onActionableButton = () => {},
  onClick = () => {},
  onError = () => {},
  onZoom = () => {},
  createdTime = undefined,
  dataPath = undefined,
  selectedCallerID = undefined,
  irpVersion,
  shouldDisplayBlankFields = false,
  collapsible = false,
  borderless = false,
  noHeader = false,
  summarized = false,
  accessToken,
  isAviveRequest = false,
}) => {
  const cardBodyRef = useRef();
  const [currentData, setCurrentData] = useState(null);

  const handleZoom = (imageURL = '') => {
    onZoom(imageURL);
  };

  return useMemo(
    () => {
      return (
        <ErrorBoundary>
          <Renderer
            data={data}
            displaySchema={display_schema}
            showDisclaimer={showDisclaimer}
            source={source}
            displaySchemaUrl={displaySchemaUrl}
            dataCategory={dataCategory}
            dataSuffix={dataSuffix}
            onError={onError}
            shouldDisplayBlankFields={shouldDisplayBlankFields}
            accessToken={accessToken}
            components={SUPPORTED_CUSTOM_COMPONENTS}
            render={props => {
              return getComponentToRender(props);
            }}
            enableCopyField
            customCopyFieldComponent={CopyFieldButton}
          >
            <TypeObject
              root
              render={props => (
                <CustomCard
                  {...props}
                  showExitButton={showExitButton}
                  currentData={currentData}
                  onFetchDataLink={onFetchDataLink}
                  onCloseDataPanel={() => setCurrentData(null)}
                  onClose={() => {
                    onActionableButton(null);
                  }}
                  cardBodyRef={cardBodyRef}
                  createdTime={createdTime}
                  dataPath={dataPath}
                  selectedCallerID={selectedCallerID}
                  collapsible={collapsible}
                  summarized={summarized}
                  borderless={borderless}
                  noHeader={noHeader}
                  onClick={onClick}
                  isAviveRequest={isAviveRequest}
                />
              )}
            />
            <TypeArray render={CustomList} />
            <TypeObject
              format="actionable-button"
              render={props => (
                <CustomActionableButton
                  {...props}
                  onClick={args => {
                    onActionableButton(args);
                  }}
                  cardBodyRef={cardBodyRef}
                  irpVersion={irpVersion}
                />
              )}
            />
            <TypeObject
              format="video"
              render={props => (
                <CustomVideo ownData={props?.ownData} irpVersion={irpVersion} />
              )}
            />
            <TypeString
              format="video-url"
              render={props => <CustomVideoRenderer {...props} />}
            />
            <TypeString
              format="image-url"
              render={props => (
                <CustomImageRenderer {...props} onZoom={handleZoom} />
              )}
            />
            <TypeString
              format="safe-image-url"
              render={props => (
                <CustomSafeImageRenderer {...props} onZoom={handleZoom} />
              )}
            />
            <TypeString
              format="data-link"
              render={props => (
                <CustomDataLink
                  {...props}
                  onClick={() => {
                    const { ownData: mafid, schema } = props;
                    setCurrentData({ mafid, schema });
                  }}
                />
              )}
            />
          </Renderer>
        </ErrorBoundary>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      data,
      display_schema,
      currentData,
      source,
      displaySchemaUrl,
      dataCategory,
      dataSuffix,
    ],
  );
};

const ConnectedEllingtonCard = ({
  dataPath = '',
  selectedCallerID,
  showDisclaimer,
  source,
  onZoom,
  onFetchDataLink,
  onActionableButton,
  onClick = () => {},
  showExitButton = true,
  summarized = false,
  accessToken = '',
}) => {
  const payloadRef = useRef(null);
  const displayedTrackingKeysRef = useRef(new Set());
  const [currentPayload, setCurrentPayload] = useState(null);

  const dataPathArray = dataPath.split('.');

  const rawPayload = useSelector(
    state => {
      const additionalData = selectADRForCall(state, selectedCallerID);
      if (!additionalData) {
        return {
          data: {},
          display_schema: {},
        };
      }
      const {
        data,
        display_schema,
        display_schema_url,
        dataCategory,
        dataSuffix,
      } = findData(additionalData, dataPathArray);
      return {
        data,
        display_schema,
        display_schema_url,
        dataCategory,
        dataSuffix,
      };
    },
    (prev, current) => JSON.stringify(prev) === JSON.stringify(current),
  );

  const { data: schemaData, isLoading: schemaLoading } =
    useGetDisplaySchemaQuery(rawPayload.display_schema_url, {
      skip: !rawPayload.display_schema_url || !!rawPayload.display_schema?.type,
    });
  if (schemaData) {
    rawPayload.display_schema = schemaData;
  }

  const { irpVersion } = useSelector(state => state.sinatra.irp);

  useEffect(() => {
    if (!rawPayload?.display_schema) {
      return;
    }

    // Don't display a card at all if properties in the schema are empty.
    // Prevents rendering just a header and no card body. NOTE, this
    // only applies to top level type object, which requires a
    // propertiesOrder field.
    const topLevelType = rawPayload.display_schema.type;
    const propertiesOrder = rawPayload.display_schema.propertiesOrder || [];
    if (topLevelType === 'object' && propertiesOrder.length <= 0) {
      return;
    }

    const validated = validatedPayload(rawPayload);
    if (JSON.stringify(payloadRef.current) === JSON.stringify(validated)) {
      return;
    }
    payloadRef.current = validated;
    setCurrentPayload(validated);

    if (!summarized) {
      const trackingKey = `${selectedCallerID}:${dataPath}`;
      if (!displayedTrackingKeysRef.current.has(trackingKey)) {
        displayedTrackingKeysRef.current.add(trackingKey);
        trackAdrDataDisplay({
          dataCategory: rawPayload.dataCategory,
          dataSuffix: rawPayload.dataSuffix,
          organization: source,
        });
      }
    }
  }, [rawPayload, selectedCallerID, dataPath, source, summarized]);

  return useMemo(
    () =>
      !schemaLoading &&
      currentPayload?.display_schema &&
      currentPayload?.data ? (
        <EllingtonCard
          data={currentPayload.data}
          display_schema={currentPayload.display_schema}
          dataPath={dataPath}
          selectedCallerID={selectedCallerID}
          showDisclaimer={showDisclaimer}
          showExitButton={showExitButton}
          source={source}
          displaySchemaUrl={rawPayload.display_schema_url}
          dataCategory={rawPayload.dataCategory}
          dataSuffix={rawPayload.dataSuffix}
          onActionableButton={onActionableButton}
          onFetchDataLink={onFetchDataLink}
          onClick={onClick}
          onZoom={onZoom}
          onError={captureExceptionWithScope}
          irpVersion={irpVersion}
          accessToken={accessToken}
          summarized={summarized}
        />
      ) : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentPayload,
      schemaLoading,
      source,
      rawPayload.display_schema_url,
      rawPayload.dataCategory,
      rawPayload.dataSuffix,
    ],
  );
};

export default ConnectedEllingtonCard;
